/**
 * CycleModal — Create a new performance cycle.
 *
 * Naming: User enters a base name (e.g., "Marketing"). The app appends
 * the period tag dynamically: "Marketing [Q1]", "Marketing [W44]", etc.
 * Uses custom Select and DatePicker components — no browser defaults.
 */
import React, { useState, useEffect, useRef } from 'react';
import CalendarRange from 'lucide-react/dist/esm/icons/calendar-range';
import Repeat from 'lucide-react/dist/esm/icons/repeat';
import { Modal, Button, TextField, Text, Alert } from '../../../components/ui';
import { Select, DatePicker } from '../../../components/ui';
import type { PerformanceCycle } from '../../../services/api/kpi';

const CYCLE_TYPES = [
  { value: 'weekly', label: 'Weekly', desc: 'Weeks in a year' },
  { value: 'monthly', label: 'Monthly', desc: 'Calendar months' },
  { value: 'quarterly', label: 'Quarterly', desc: 'Q1, Q2, Q3, Q4' },
  { value: 'semi_annual', label: 'Semi-Annual', desc: 'H1 and H2' },
  { value: 'annual', label: 'Annual', desc: 'Full year' },
  { value: 'custom', label: 'Custom', desc: 'Set your own dates' },
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

interface CycleModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingCycles: PerformanceCycle[];
  onCreate: (data: { name: string; cycle_type: string; start_date: string; end_date: string }) => Promise<any>;
}

/** Compute the ISO week number for a date. */
function getISOWeek(d: Date): number {
  const target = new Date(d.valueOf());
  const dayNum = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNum + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  }
  return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
}

/** Build the period tag that gets appended to the base name. */
function getPeriodTag(type: string, startDateStr: string): string {
  if (!startDateStr) return '';
  const d = new Date(startDateStr + 'T00:00:00');
  const y = d.getFullYear();
  const m = d.getMonth();
  switch (type) {
    case 'weekly':
      return `[W${getISOWeek(d)}]`;
    case 'monthly':
      return `[${MONTHS[m].slice(0, 3)}]`;
    case 'quarterly':
      return `[Q${Math.floor(m / 3) + 1}]`;
    case 'semi_annual':
      return `[H${m < 6 ? 1 : 2}]`;
    case 'annual':
      return `[Y${y}]`;
    default:
      return '';
  }
}

function suggestDates(type: string): { start: string; end: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const pad = (n: number) => String(n).padStart(2, '0');

  switch (type) {
    case 'weekly': {
      const day = now.getDay();
      const monday = new Date(now);
      monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return {
        start: `${monday.getFullYear()}-${pad(monday.getMonth() + 1)}-${pad(monday.getDate())}`,
        end: `${sunday.getFullYear()}-${pad(sunday.getMonth() + 1)}-${pad(sunday.getDate())}`,
      };
    }
    case 'monthly': {
      const lastDay = new Date(y, m + 1, 0).getDate();
      return { start: `${y}-${pad(m + 1)}-01`, end: `${y}-${pad(m + 1)}-${pad(lastDay)}` };
    }
    case 'quarterly': {
      const qStart = Math.floor(m / 3) * 3;
      const lastDay = new Date(y, qStart + 3, 0).getDate();
      return { start: `${y}-${pad(qStart + 1)}-01`, end: `${y}-${pad(qStart + 3)}-${pad(lastDay)}` };
    }
    case 'semi_annual': {
      const hStart = m < 6 ? 0 : 6;
      const hEnd = m < 6 ? 5 : 11;
      const lastDay = new Date(y, hEnd + 1, 0).getDate();
      return { start: `${y}-${pad(hStart + 1)}-01`, end: `${y}-${pad(hEnd + 1)}-${pad(lastDay)}` };
    }
    case 'annual':
      return { start: `${y}-01-01`, end: `${y}-12-31` };
    default:
      return { start: '', end: '' };
  }
}

const CycleModal: React.FC<CycleModalProps> = ({ isOpen, onClose, existingCycles, onCreate }) => {
  const nameRef = useRef<HTMLInputElement>(null);
  const [cycleType, setCycleType] = useState('quarterly');
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [recurring, setRecurring] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const dates = suggestDates(cycleType);
      setName('');
      setStartDate(dates.start);
      setEndDate(dates.end);
      setRecurring(true);
      setError(null);
      setSaving(false);
      setTimeout(() => nameRef.current?.focus(), 100);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTypeChange = (type: string) => {
    setCycleType(type);
    if (type !== 'custom') {
      const dates = suggestDates(type);
      setStartDate(dates.start);
      setEndDate(dates.end);
    }
  };

  const periodTag = getPeriodTag(cycleType, startDate);
  const fullName = name.trim()
    ? `${name.trim()} ${periodTag}`.trim()
    : periodTag;

  const handleCreate = async () => {
    if (!name.trim() || !startDate || !endDate) {
      setError('Please fill in all fields');
      return;
    }
    if (endDate <= startDate) {
      setError('End date must be after start date');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onCreate({
        name: name.trim(),
        cycle_type: cycleType,
        start_date: startDate,
        end_date: endDate,
      });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create cycle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <Modal.Header
        title="New Performance Cycle"
        icon={<CalendarRange size={16} />}
        onClose={onClose}
      />

      <Modal.Body className="space-y-5">
        {/* Cycle Type — radio-style cards */}
        <div>
          <Text variant="label" className="mb-2">Cycle Type</Text>
          <div className="grid grid-cols-3 gap-2">
            {CYCLE_TYPES.map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => handleTypeChange(t.value)}
                className={`p-3 rounded-xl text-left transition-all cursor-pointer border outline-none
                  ${cycleType === t.value
                    ? 'bg-brand/10 border-brand/25'
                    : 'bg-transparent border-subtle hover:bg-surface-primary hover:border-default'
                  }
                `}
              >
                <span className={`text-xs font-medium block ${cycleType === t.value ? 'text-content' : 'text-content-secondary'}`}>
                  {t.label}
                </span>
                <span className="text-[10px] text-content-faint block mt-0.5">{t.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Name */}
        <div>
          <TextField
            ref={nameRef}
            label="Cycle Name"
            value={name}
            onChange={(e) => { setName(e.target.value); if (error) setError(null); }}
            placeholder="e.g., Marketing, Engineering, Company..."
          />
          {fullName && (
            <p className="text-[11px] text-content-muted mt-1.5">
              Will display as: <span className="text-brand/60 font-medium">{fullName}</span>
            </p>
          )}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Text variant="label" className="mb-1.5">Start Date</Text>
            <DatePicker
              value={startDate}
              onChange={(v) => { setStartDate(v); if (error) setError(null); }}
              placeholder="Pick start date"
            />
          </div>
          <div>
            <Text variant="label" className="mb-1.5">End Date</Text>
            <DatePicker
              value={endDate}
              onChange={(v) => { setEndDate(v); if (error) setError(null); }}
              placeholder="Pick end date"
            />
          </div>
        </div>

        {/* Recurring toggle */}
        {cycleType !== 'custom' && (
          <button
            type="button"
            onClick={() => setRecurring(!recurring)}
            className={`flex items-center gap-3 w-full p-3 rounded-xl text-left transition-all cursor-pointer border outline-none
              ${recurring
                ? 'bg-status-success/[0.06] border-status-success/20'
                : 'bg-transparent border-subtle hover:bg-surface-primary'
              }
            `}
          >
            <Repeat size={16} className={recurring ? 'text-status-success' : 'text-content-faint'} />
            <div>
              <span className={`text-xs font-medium block ${recurring ? 'text-content' : 'text-content-tertiary'}`}>
                Auto-advance to next cycle
              </span>
              <span className="text-[10px] text-content-faint block mt-0.5">
                When this cycle closes, automatically create the next period and carry over active KPIs
              </span>
            </div>
          </button>
        )}

        {error && <Alert variant="error">{error}</Alert>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="ghost" size="lg" onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          size="lg"
          onClick={handleCreate}
          loading={saving}
          disabled={!name.trim() || !startDate || !endDate}
        >
          Create Cycle
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default React.memo(CycleModal);
